/* RVP with QC › Merchant QC Editor (create + edit) — v5 mockup UI (rvpQc.css).
   Loads the merchant's QC config (§3.1) and saves it (§3.2). Parameter list = live master
   list (§2.1) keyed by `code`; merchant header from the cached merchant API.

   Each QC mode keeps its OWN data (draft.standard / draft.reason), so switching cards shows
   only that mode's data — a card is populated only when it matches the mode from the response. */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Select, MenuItem, Box, TextField, Alert, Chip, Stack, Typography, Accordion, AccordionSummary, AccordionDetails, IconButton, Switch, Tooltip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import PageTitle from '../../../components/PageTitle'
import ProzoDataGrid from '../../../common/ProzoDataGrid'
import { CellLine, CodeCell, CodeText, humanize, hasMeaningfulDesc, searchFieldSx, SearchAdornment, selectSx, selectMenuProps, SearchSelect, gridSx } from './gridCells'
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllCourierPartnersCachedQuery } from '../../../redux/commonApiCalls'
import { getGeneric, postGeneric } from '../../../common/constants'
import { useQcParameterMasterQuery, useMerchantQcConfigQuery, useSaveMerchantQcConfigMutation } from '../../../redux/rvpQcApiSlice'
import './rvpQc.css'

// ── Accordion section styling — each editor section is a collapsible card so the page is no
// longer one long crammed scroll. Inner `.card` chrome is neutralised inside the details so we
// don't get a card-inside-a-card; only the accordion is the visible container.
const accCardSx = {
  border: '1px solid #E2E8F0', borderRadius: '10px !important', mb: 1.5,
  boxShadow: '0 1px 3px rgba(0,0,0,.06)', bgcolor: '#fff', overflow: 'hidden',
  '&:before': { display: 'none' },
}
const accSummarySx = { px: 2.5, minHeight: 56, '& .MuiAccordionSummary-content': { my: 1.25 } }
const accTitleSx = { fontSize: 15, fontWeight: 700, color: 'primary.dark', lineHeight: 1.2 }
const accSubSx = { fontSize: 12, color: 'text.secondary', mt: 0.25 }
// Setup section keeps its inner h2s as sub-headings; just strips card chrome + spaces blocks.
const accDetailsSx = { px: 2.5, pt: 0, pb: 2.5, '& .card': { border: 'none', boxShadow: 'none', p: 0, mb: 2 }, '& .card:last-child': { mb: 0 } }
// Coverage/Fallback are single-purpose — hide the inner card title (the summary already names it).
const accDetailsBareSx = { px: 2.5, pt: 0, pb: 2.5, '& .card': { border: 'none', boxShadow: 'none', p: 0, m: 0 }, '& .card h2': { display: 'none' } }

// Subtle status chip on each accordion header — stays visible when the section is collapsed so the
// section's current state (QC type + count, courier count, approval) reads at a glance.
const statusChipSx = { height: 22, fontSize: 11.5, fontWeight: 600, borderRadius: '6px', '& .MuiChip-label': { px: 1 }, '& .MuiChip-icon': { fontSize: 15, ml: 0.5 } }
const neutralChipSx = { ...statusChipSx, borderColor: '#E2E8F0', color: 'text.secondary', bgcolor: '#F8FAFC' }
const amberChipSx = { ...statusChipSx, borderColor: 'rgba(183,129,3,.35)', color: 'warning.dark', bgcolor: 'rgba(183,129,3,.08)', '& .MuiChip-icon': { fontSize: 15, ml: 0.5, color: 'warning.dark' } }
const successChipSx = { ...statusChipSx, borderColor: 'rgba(16,185,129,.4)', color: 'success.dark', bgcolor: 'rgba(16,185,129,.08)', '& .MuiChip-icon': { fontSize: 15, ml: 0.5, color: 'success.main' } }
// Tinted square that fronts each header — faintly blue when its section is open, neutral grey when collapsed.
const secIconSx = (open) => ({
  width: 34, height: 34, borderRadius: '8px', flexShrink: 0,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  bgcolor: open ? 'primary.lighter' : '#F1F5F9', color: open ? 'primary.dark' : 'text.secondary',
  transition: 'background-color .2s, color .2s', '& svg': { fontSize: 19 },
})

// Shared accordion header: tinted section icon · title/subtitle (+ optional ⓘ) · right-aligned status chip.
// `open` drives the faint accent (blue icon tint + blue title) so the active section stands out.
function SectionSummary({ icon, title, subtitle, open, info, chip }) {
  return (
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accSummarySx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', pr: 1 }}>
        <Box sx={secIconSx(open)}>{icon}</Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ ...accTitleSx, color: open ? 'primary.dark' : 'text.primary' }}>{title}</Typography>
            <Typography sx={accSubSx}>{subtitle}</Typography>
          </Box>
          {info}
        </Box>
        <Box sx={{ flex: 1 }} />
        {chip}
      </Box>
    </AccordionSummary>
  )
}
// Left-accent added to the open section's card (thicker blue left border), neutral when collapsed.
const accCardOpenSx = (open) => (open ? { ...accCardSx, borderLeftWidth: '3px', borderLeftColor: 'primary.main' } : accCardSx)

/* RVP-QC couriers (to show which couriers support each parameter) — same source as the
   Courier QC Master: serviceTypePrimary === 'REV_REG_QC', grouped by parent. */
const RVP_QC_COURIER_PAYLOAD = {
  andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
  offset: 0, limit: 1000, skipCount: true,
  includes: 'id,name,parent,cp_id,serviceTypePrimary',
}

/* #10 — brand-level control for how the merchant sees their RVP-QC setup in the merchant panel:
   View only / View and edit / Do not show. Persisted as merchantPanelAccess on the QC-return
   config via Save QC setup (§3.2) and read back from it (§3.1) — no separate write. */
const VIS_OPTIONS = [
  { v: 'VIEW_ONLY', label: 'View only', desc: 'Merchant sees the setup, read-only.', icon: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" /></> },
  { v: 'VIEW_EDIT', label: 'View and edit', desc: 'Merchant can see and edit their setup.', icon: <><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" /></> },
  { v: 'HIDDEN', label: 'Do not show', desc: 'Not shown in the merchant panel.', icon: <><path d="M17.9 17.9A10.1 10.1 0 0 1 12 20C5.5 20 2 13 2 13a18.5 18.5 0 0 1 5.1-5.9M9.9 4.2A9.1 9.1 0 0 1 12 4c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.2 3.2M1 1l22 22" /></> },
]
// map between the UI mode and the payload/config value (merchantPanelAccess): VIEW / EDIT / NONE
const PANEL_TO_ACCESS = { VIEW_ONLY: 'VIEW', VIEW_EDIT: 'EDIT', HIDDEN: 'NONE' }
const ACCESS_TO_PANEL = { VIEW: 'VIEW_ONLY', EDIT: 'VIEW_EDIT', NONE: 'HIDDEN' }
const MAND_TIP = 'Mandatory = shipment creation fails if this check’s data is missing. Optional = skipped if missing.'

const TYPES = [
  { key: 'standard', t: 'Standard QC', d: 'One checklist runs on every return.', icon: <><path d="M12 3l8 3v6c0 4.5-3.2 7.8-8 9-4.8-1.2-8-4.5-8-9V6z" /><path d="M9 12l2 2 4-4" /></> },
  { key: 'reason', t: 'Reason-based QC', d: "Checks depend on the customer's return reason.", icon: <path d="M4 7h16M4 12h10M4 17h6" /> },
  { key: 'nonqc', t: 'Non-QC', d: 'No doorstep checks — every return is accepted.', icon: <><circle cx="12" cy="12" r="9" /><path d="M6 6l12 12" /></> },
]
const Tip = ({ title }) => <span className="tip" title={title}>?</span>
const initials = (n) => (n || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '—'

const EMPTY = () => ({ standard: { sel: [], mand: {}, hard: {} }, reason: { reasons: [] } })

/* §3.1 config → editor draft (mode-separated, keyed by parameter code) */
function configToDraft(cfg) {
  const c = cfg && typeof cfg === 'object' ? cfg : {}
  const base = EMPTY()
  if (c.mode === 'STANDARD') {
    const sp = c.standardParameters || []
    base.standard = { sel: sp.map((p) => p.code), mand: Object.fromEntries(sp.map((p) => [p.code, !!p.mandatory])), hard: Object.fromEntries(sp.map((p) => [p.code, p.checkType !== 'SOFT'])) }
    return { type: 'standard', ...base }
  }
  if (c.mode === 'PARAM') {
    const rm = c.reasonMap || []
    base.reason = { reasons: rm.map((r, i) => ({ id: 'r' + i, label: r.returnReason || '', params: (r.params || []).map((p) => ({ code: p.code, mandatory: !!p.mandatory, hard: p.checkType !== 'SOFT' })) })) }
    return { type: 'reason', ...base }
  }
  if (c.mode === 'NONE') return { type: 'nonqc', ...base }
  return { type: 'standard', ...base } // never configured
}
/* editor draft → §3.2 body (uses only the active mode's data) */
function draftToBody(d) {
  if (d.type === 'nonqc') return { mode: 'NONE' }
  if (d.type === 'standard') return { mode: 'STANDARD', standardParameters: d.standard.sel.map((code) => ({ code, mandatory: !!d.standard.mand[code], checkType: d.standard.hard?.[code] !== false ? 'HARD' : 'SOFT' })) }
  return { mode: 'PARAM', reasonMap: d.reason.reasons.map((r) => ({ returnReason: r.label, params: (r.params || []).map((p) => ({ code: p.code, mandatory: !!p.mandatory, checkType: p.hard !== false ? 'HARD' : 'SOFT' })) })) }
}

/* Searchable merchant combobox — type to filter, click to pick. */
function MerchantPicker({ merchants, value, onChange }) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)
  const label = (m) => `${m.name}${m.companyName ? ` · ${m.companyName}` : ''}`
  const selected = merchants.find((m) => m.id === value)
  const list = q.trim() ? merchants.filter((m) => label(m).toLowerCase().includes(q.trim().toLowerCase())) : merchants
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  return (
    <div className="msel" ref={wrapRef}>
      <input className="msel-input" placeholder="Search merchant…"
        value={open ? q : (selected ? label(selected) : '')}
        onChange={(e) => { setQ(e.target.value); if (!open) setOpen(true) }}
        onFocus={() => { setOpen(true); setQ('') }} />
      {open && (
        <div className="msel-drop">
          {list.length ? list.map((m) => (
            <div key={m.id} className={`msel-opt ${m.id === value ? 'on' : ''}`}
              onMouseDown={() => { onChange(m.id); setOpen(false); setQ('') }}>{label(m)}</div>
          )) : <div className="msel-empty">No merchants match “{q}”.</div>}
        </div>
      )}
    </div>
  )
}

export default function RvpQcEditor() {
  const { merchantId } = useParams()
  const navigate = useNavigate()
  const isNew = merchantId === 'new'

  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const [pickedId, setPickedId] = useState('')
  const effectiveId = isNew ? pickedId : merchantId
  const merchant = merchants.find((m) => m.id === effectiveId)
  const { data: paramsRaw } = useQcParameterMasterQuery({ activeOnly: true })
  const params = Array.isArray(paramsRaw) ? paramsRaw : (Array.isArray(paramsRaw?.data) ? paramsRaw.data : [])
  // RVP-QC couriers (by parent) — used to show per-parameter courier support
  const { data: courierRows = [] } = useGetAllCourierPartnersCachedQuery(RVP_QC_COURIER_PAYLOAD)
  const couriers = useMemo(() => {
    const seen = new Map()
    ;(courierRows || [])
      .filter((c) => c?.serviceTypePrimary === 'REV_REG_QC' && c?.parent)
      .forEach((c) => { if (!seen.has(c.parent)) seen.set(c.parent, c?.cpId ?? c?.cp_id) })
    return [...seen.entries()].map(([name, cpId]) => ({ name, cpId })).sort((a, b) => String(a.name).localeCompare(String(b.name)))
  }, [courierRows])
  const { data: cfg, isFetching: cfgLoading, isSuccess, isError, refetch } = useMerchantQcConfigQuery(effectiveId, { skip: !effectiveId })
  const [saveConfig, { isLoading: isSaving }] = useSaveMerchantQcConfigMutation()

  const [draft, setDraft] = useState(null)
  // Editor is organised into collapsible accordion sections (QC Setup · Courier Coverage ·
  // Fallback) instead of one long scroll. Panels are INDEPENDENT — 'setup' open by default, the
  // others collapsed, and the user can open any/all of them at once.
  const [openPanels, setOpenPanels] = useState({ setup: true })
  const toggleAcc = (panel) => (e, isOpen) => setOpenPanels((s) => ({ ...s, [panel]: isOpen }))
  // #10 — merchant-panel visibility (View only / View and edit / Do not show). Local state;
  // seeded from the saved config below and persisted via merchantPanelAccess on Save QC setup.
  const [panelMode, setPanelMode] = useState('VIEW_ONLY')
  // seed once per merchant so a card-click refetch doesn't wipe an in-progress selection
  const seededFor = useRef(null)
  useEffect(() => {
    if (!effectiveId) { setDraft(null); setPanelMode('VIEW_ONLY'); seededFor.current = null; return }
    if (seededFor.current === effectiveId) return
    if (isSuccess) { setDraft(configToDraft(cfg)); setPanelMode(ACCESS_TO_PANEL[cfg?.merchantPanelAccess] || 'VIEW_ONLY'); seededFor.current = effectiveId }
    else if (isError) { setDraft(configToDraft(null)); setPanelMode('VIEW_ONLY'); seededFor.current = effectiveId }
  }, [effectiveId, isSuccess, isError, cfg])

  // #7 — merchant's reverse RVP-QC courier allocation (to highlight allocated partners in the
  // availability section). Source: GET allocation/merchants/{oid}/allocation → reverseRules
  // whose qcModes include QC. source==='none'/reverse off → no allocation uploaded.
  const [alloc, setAlloc] = useState(null)
  useEffect(() => {
    if (!effectiveId) { setAlloc(null); return }
    getGeneric(`allocation/merchants/${effectiveId}/allocation`)
      .then((res) => setAlloc(res?.data ?? null))
      .catch(() => setAlloc(null))
  }, [effectiveId])
  const allocatedCouriers = useMemo(() => {
    const rules = Array.isArray(alloc?.reverseRules) ? alloc.reverseRules : []
    const names = new Set()
    rules
      .filter((r) => (r.qcModes || []).some((m) => ['WITHQC', 'QC'].includes(String(m).toUpperCase())))
      .forEach((r) => (r.couriers || []).forEach((c) => c && names.add(String(c))))
    return names
  }, [alloc])
  const allocUploaded = !!alloc && alloc.source !== 'none' && !!alloc.reverseEnabled && allocatedCouriers.size > 0

  const ready = !!effectiveId && !!draft && !cfgLoading

  // once a merchant has a saved mode, lock the type to it — the other cards are disabled.
  // (empty/never-configured → savedType null → all cards selectable.)
  const savedMode = cfg && typeof cfg === 'object' ? cfg.mode : null
  const savedType = savedMode === 'STANDARD' ? 'standard' : savedMode === 'PARAM' ? 'reason' : savedMode === 'NONE' ? 'nonqc' : null
  const savedTypeLabel = TYPES.find((t) => t.key === savedType)?.t   // e.g. "Reason-based QC"

  const patch = (fn) => setDraft((d) => { const n = JSON.parse(JSON.stringify(d)); fn(n); return n })
  // card select: switch mode locally + re-GET the merchant config (§3.1). POST only on Save.
  const setType = (type) => { patch((d) => { d.type = type }); if (effectiveId) refetch() }

  // standard-mode mutators
  const toggleStd = (code) => patch((d) => {
    d.standard.hard = d.standard.hard || {}
    if (d.standard.sel.includes(code)) { d.standard.sel = d.standard.sel.filter((x) => x !== code); delete d.standard.mand[code]; delete d.standard.hard[code] }
    else { d.standard.sel.push(code); d.standard.mand[code] = true; d.standard.hard[code] = true }
  })
  const setStdMand = (code, val) => patch((d) => { d.standard.mand[code] = val })
  const setStdHard = (code, val) => patch((d) => { d.standard.hard = d.standard.hard || {}; d.standard.hard[code] = val })
  // reason-mode mutators — each reason owns its own params + per-param mandatory flag
  const addReason = () => patch((d) => { d.reason.reasons.push({ id: 'r' + Date.now().toString(36), label: '', params: [] }) })
  const removeReason = (rid) => patch((d) => { d.reason.reasons = d.reason.reasons.filter((r) => r.id !== rid) })
  const setReasonLabel = (rid, label) => patch((d) => { const r = d.reason.reasons.find((x) => x.id === rid); if (r) r.label = label })
  const addReasonParam = (rid, code) => patch((d) => { const r = d.reason.reasons.find((x) => x.id === rid); if (r && code && !r.params.some((p) => p.code === code)) r.params.push({ code, mandatory: true, hard: true }) })
  const removeReasonParam = (rid, code) => patch((d) => { const r = d.reason.reasons.find((x) => x.id === rid); if (r) r.params = r.params.filter((p) => p.code !== code) })
  const setReasonParamMand = (rid, code, val) => patch((d) => { const r = d.reason.reasons.find((x) => x.id === rid); const p = r && r.params.find((x) => x.code === code); if (p) p.mandatory = val })
  const setReasonParamHard = (rid, code, val) => patch((d) => { const r = d.reason.reasons.find((x) => x.id === rid); const p = r && r.params.find((x) => x.code === code); if (p) p.hard = val })

  // #8 · fallback ladder consent — lifted here so Save can read it and gate on approval.
  const mandatoryCodes = useMemo(() => {
    if (!draft) return []
    if (draft.type === 'standard') return draft.standard.sel.filter((c) => draft.standard.mand[c])
    if (draft.type === 'reason') return [...new Set(draft.reason.reasons.flatMap((r) => (r.params || []).filter((p) => p.mandatory).map((p) => p.code)))]
    return []
  }, [draft])
  const N = new Set(mandatoryCodes).size
  const [fbAccepted, setFbAccepted] = useState({}) // key ('NONQC' or count k) → bool; undefined = NOT accepted (off by default)
  const [fbApproved, setFbApproved] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  const fbIsAcc = (key) => fbAccepted[key] === true
  // Single-threshold ladder, not independent checkboxes: accepting a looser tier (lower k)
  // implies every stricter tier above it (closer to N) is acceptable too — you'd obviously take
  // a BETTER match than the one you just said you'd accept. Rejecting a tier likewise rejects
  // every looser tier below it. This keeps the visible toggles in sync with minAcceptedK/maxRelax
  // below, which already only cares about the single lowest accepted k, not per-row state.
  const fbToggle = (key) => {
    if (key === 'NONQC') { setFbAccepted((a) => ({ ...a, NONQC: a.NONQC === true ? false : true })); setFbApproved(false); return }
    const k = Number(key)
    setFbAccepted((a) => {
      const next = { ...a }
      if (a[key] !== true) {
        for (let i = k; i < N; i++) next[String(i)] = true
      } else {
        for (let i = 1; i <= k; i++) next[String(i)] = false
      }
      return next
    })
    setFbApproved(false)
  }
  const nonQcAccepted = fbIsAcc('NONQC')
  let minAcceptedK = N
  for (let k = N - 1; k >= 1; k--) if (fbIsAcc(String(k))) minAcceptedK = k
  const maxRelax = N === 0 ? 0 : N - minAcceptedK
  const needsApproval = !!draft && draft.type !== 'nonqc' && N > 0
  useEffect(() => { setFbAccepted({}); setFbApproved(false) }, [effectiveId])
  useEffect(() => { setFbApproved(false) }, [N])

  // #9 · serviceability advisor (§3.3) — eligible couriers + distinct pickup-pincode coverage per
  // ladder step, for the merchant's allocated RVP couriers and the proposed mandatory set.
  const [advisor, setAdvisor] = useState(null)
  const [advLoading, setAdvLoading] = useState(false)
  const mKey = [...new Set(mandatoryCodes)].sort().join(',')
  useEffect(() => {
    if (!effectiveId || !mKey) { setAdvisor(null); return undefined }
    let cancelled = false
    setAdvLoading(true)
    postGeneric(`config/merchant/${effectiveId}/qc-return/advisor`, { mandatoryParamCodes: mKey.split(',') })
      .then((res) => { if (!cancelled) setAdvisor(res?.data ?? null) })
      .catch(() => { if (!cancelled) setAdvisor(null) })
      .finally(() => { if (!cancelled) setAdvLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveId, mKey])

  const onSave = async () => {
    if (!ready) return
    if (draft.type === 'reason') {
      const labels = draft.reason.reasons.map((r) => r.label.trim().toLowerCase()).filter(Boolean)
      if (new Set(labels).size !== labels.length) { window.alert('Return reasons must be unique (verbatim). Please remove the duplicate before saving.'); return }
    }
    if (needsApproval && !fbApproved) { window.alert('Please review and approve the allocation fallback sequence (Step 4) before saving.'); return }
    const body = draftToBody(draft)
    body.merchantPanelAccess = PANEL_TO_ACCESS[panelMode] || 'VIEW'
    if (draft.type !== 'nonqc') {
      body.allowNonQcFallback = nonQcAccepted
      body.maxMandatoryRelaxation = maxRelax
    }
    try { await saveConfig({ id: effectiveId, body }).unwrap(); navigate('/admin/rvp-qc') }
    catch (e) { window.alert(e?.message || 'Failed to save QC setup.') }
  }

  return (
    <div className="rvpqc">
      <PageTitle>RVP with QC · Merchant QC Setup</PageTitle>

      {isNew ? (
        <div className="card mselcard">
          <h2>Select merchant</h2>
          <div className="ctx">Create a QC configuration for a merchant — pick one to begin.</div>
          <div className="field" style={{ maxWidth: 440 }}>
            <label>Merchant <span style={{ color: '#DC2626' }}>*</span></label>
            <MerchantPicker merchants={merchants} value={pickedId} onChange={setPickedId} />
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="mhead">
            <div className="av">{initials(merchant?.name)}</div>
            <div><div className="nm2">{merchant?.name || 'Merchant'}</div><div className="sub">{merchant?.companyName || merchantId}</div></div>
            <div className="spacer" />
            <button className="linkbtn" onClick={() => navigate('/admin/rvp-qc')}>← All merchants</button>
          </div>
        </div>
      )}

      {isNew && !pickedId && (
        <div className="card">
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 7, px: 3 }}>
            <Box sx={{ width: 76, height: 76, borderRadius: '50%', bgcolor: 'primary.lighter', color: 'primary.dark', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
              <FactCheckOutlinedIcon sx={{ fontSize: 38 }} />
            </Box>
            <Typography sx={{ fontSize: 17, fontWeight: 700, color: 'text.primary' }}>Select a merchant to begin</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1, maxWidth: 480, lineHeight: 1.6 }}>
              Choose a merchant from the dropdown above to view and configure its QC setup.
            </Typography>
            <Stack direction="row" sx={{ mt: 3, flexWrap: 'wrap', justifyContent: 'center', gap: 1.25 }}>
              <Chip icon={<TuneOutlinedIcon />} label="QC type" variant="outlined" sx={{ fontWeight: 600 }} />
              <Chip icon={<ListAltOutlinedIcon />} label="Parameters" variant="outlined" sx={{ fontWeight: 600 }} />
              <Chip icon={<AccountTreeOutlinedIcon />} label="Return-reason matrix" variant="outlined" sx={{ fontWeight: 600 }} />
            </Stack>
          </Box>
        </div>
      )}

      {effectiveId && !ready && <div className="card"><div className="ctx" style={{ marginBottom: 0 }}>Loading QC configuration…</div></div>}

      {ready && <>
      {/* ── QC Setup ── */}
      <Accordion disableGutters expanded={!!openPanels.setup} onChange={toggleAcc('setup')} sx={accCardOpenSx(!!openPanels.setup)}>
        <SectionSummary
          open={!!openPanels.setup}
          icon={<TuneOutlinedIcon />}
          title="QC Setup"
          subtitle="Merchant-panel visibility, QC type and the checks that run"
          info={
            <Tooltip title="A Mandatory check fails shipment creation if its data is missing (e.g. a mandatory image with no photo captured). An Optional check is simply skipped and creation continues. New checks default to Mandatory.">
              <InfoOutlinedIcon onClick={(e) => e.stopPropagation()} sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
            </Tooltip>
          }
          chip={
            <Chip size="small" variant="outlined" sx={neutralChipSx}
              label={draft.type === 'nonqc'
                ? 'Non-QC'
                : draft.type === 'reason'
                  ? `Reason-based · ${draft.reason.reasons.length} reason${draft.reason.reasons.length === 1 ? '' : 's'}`
                  : `Standard · ${draft.standard.sel.length} check${draft.standard.sel.length === 1 ? '' : 's'}`}
            />
          }
        />
        <AccordionDetails sx={accDetailsSx}>
          {/* Merchant-panel visibility — rarely changed, so a compact one-line control instead of a big card block. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pb: 1.5, mb: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}>Merchant-panel visibility</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>How this merchant sees the QC setup in their panel</Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <Select size="small" value={panelMode} onChange={(e) => setPanelMode(e.target.value)}
              sx={{ ...selectSx, minWidth: 210 }} MenuProps={selectMenuProps}>
              {VIS_OPTIONS.map((o) => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
            </Select>
          </Box>

          <div className="card">
            <h2>QC type</h2>
            <div className="ctx">QC applies <b>globally</b> to this merchant’s reverse pickups (all couriers). Choose one mode.</div>
            <div className="types">
              {TYPES.map((t) => {
                const locked = savedType && t.key !== savedType
                return (
                  <div key={t.key} className={`tcard ${draft.type === t.key ? 'on' : ''} ${locked ? 'disabled' : ''}`}
                    title={locked ? `This merchant is configured as ${savedTypeLabel}; the QC type can't be changed here.` : undefined}
                    onClick={() => { if (!locked) setType(t.key) }}>
                    <div className="tic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{t.icon}</svg></div>
                    <div><div className="t">{t.t}</div><div className="d">{t.d}</div></div>
                  </div>
                )
              })}
            </div>
            {savedType && <div className="ctx" style={{ marginTop: 10, marginBottom: 0 }}>QC type is locked to <b>{savedTypeLabel}</b> for this merchant.</div>}
          </div>

          {draft.type === 'nonqc' && (
            <div className="card">
              <h2>Non-QC</h2>
              <div className="ctx">This merchant is set to <b>Non-QC</b>.</div>
              <Alert severity="info">No QC parameters are mapped. Reverse pickups for this merchant will be accepted <b>without any doorstep quality checks</b>. Switch the type above to Standard or Reason-based to configure checks.</Alert>
            </div>
          )}
          {draft.type === 'standard' && <StandardCard params={params} data={draft.standard} toggleStd={toggleStd} setMand={setStdMand} setHard={setStdHard} />}
          {draft.type === 'reason' &&
            <ReasonConfigCard params={params} data={draft.reason} couriers={couriers}
              addReason={addReason} removeReason={removeReason} setReasonLabel={setReasonLabel}
              addReasonParam={addReasonParam} removeReasonParam={removeReasonParam} setReasonParamMand={setReasonParamMand} setReasonParamHard={setReasonParamHard} />}
        </AccordionDetails>
      </Accordion>

      {/* ── Courier Coverage ── */}
      {draft.type !== 'nonqc' && (
        <Accordion disableGutters expanded={!!openPanels.coverage} onChange={toggleAcc('coverage')} sx={accCardOpenSx(!!openPanels.coverage)}>
          <SectionSummary
            open={!!openPanels.coverage}
            icon={<LocalShippingOutlinedIcon />}
            title="Courier Coverage"
            subtitle="Which RVP-QC couriers can perform each selected check"
            info={
              <Tooltip title="Shows which RVP-QC couriers can perform each selected check. When the merchant has a reverse allocation, its couriers are tagged “allocated” and the rest are shown but not allocated. Courier-native parameter names are intentionally hidden.">
                <InfoOutlinedIcon onClick={(e) => e.stopPropagation()} sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
              </Tooltip>
            }
            chip={<Chip size="small" variant="outlined" sx={neutralChipSx} label={`${couriers.length} courier${couriers.length === 1 ? '' : 's'}`} />}
          />
          <AccordionDetails sx={accDetailsBareSx}>
            <AvailabilityCard
              selectedCodes={draft.type === 'standard'
                ? draft.standard.sel
                : [...new Set(draft.reason.reasons.flatMap((r) => (r.params || []).map((p) => p.code)))]}
              params={params} couriers={couriers}
              allocatedCouriers={allocatedCouriers} allocUploaded={allocUploaded}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Fallback ── */}
      {draft.type !== 'nonqc' && (
        <Accordion disableGutters expanded={!!openPanels.fallback} onChange={toggleAcc('fallback')} sx={accCardOpenSx(!!openPanels.fallback)}>
          <SectionSummary
            open={!!openPanels.fallback}
            icon={<LayersOutlinedIcon />}
            title="Fallback"
            subtitle="Tries full QC first, then steps down through the levels you allow — toggle each below"
            chip={fbApproved
              ? <Chip size="small" variant="outlined" icon={<CheckCircleOutlineIcon />} label="Approved" sx={successChipSx} />
              : <Chip size="small" variant="outlined" icon={<InfoOutlinedIcon />} label="Approval pending" sx={amberChipSx} />}
          />
          <AccordionDetails sx={accDetailsBareSx}>
            <FallbackCard
              mandatoryCodes={mandatoryCodes}
              params={params} couriers={couriers}
              advisor={advisor} advLoading={advLoading}
              isAcc={fbIsAcc} onToggle={fbToggle} approved={fbApproved} onReview={() => setApproveOpen(true)}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <div className="sticky-actions">
        <button className="btn muted" onClick={() => navigate('/admin/rvp-qc')}>Cancel</button>
        <button className="btn" disabled={isSaving || (needsApproval && !fbApproved)}
          title={needsApproval && !fbApproved ? 'Review & approve the allocation fallback sequence first' : undefined}
          onClick={onSave}>{isSaving ? 'Saving…' : 'Save QC setup'}</button>
      </div>
      </>}

      {approveOpen && (
        <div className="rvpqc-modal-ov" onClick={() => setApproveOpen(false)}>
          <div className="rvpqc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mhd">
              <div className="ic">✓</div>
              <div><h3>Review allocation fallback sequence</h3><div className="sub">Confirm the fallback steps you’re consenting to for this merchant. Saving stays locked until you approve.</div></div>
              <button className="x" onClick={() => setApproveOpen(false)}>×</button>
            </div>
            <div className="mbd">
              <ul style={{ margin: '0 0 12px', paddingLeft: 18 }}>
                <li><b>Full QC</b> — {N} of {N} check{N > 1 ? 's' : ''} <span style={{ color: 'var(--muted)' }}>(always attempted first)</span></li>
                {Array.from({ length: Math.max(0, N - 1) }, (_, i) => N - 1 - i).map((k) => (
                  <li key={k}>{k} of {N} check{k > 1 ? 's' : ''} — <b style={{ color: fbIsAcc(String(k)) ? '#15803D' : '#B91C1C' }}>{fbIsAcc(String(k)) ? 'Accepted' : 'Not accepted'}</b></li>
                ))}
                <li>Non-QC pickup <span style={{ color: 'var(--muted)' }}>(last resort)</span> — <b style={{ color: nonQcAccepted ? '#15803D' : '#B91C1C' }}>{nonQcAccepted ? 'Accepted' : 'Not accepted — orders may become non-serviceable'}</b></li>
              </ul>
            </div>
            <div className="mft">
              <button className="btn muted" onClick={() => setApproveOpen(false)}>Cancel</button>
              <button className="btn" onClick={() => { setFbApproved(true); setApproveOpen(false) }}>Confirm &amp; approve</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const MSeg = ({ mand, onMand }) => (
  <div className="mseg">
    <button data-v="mand" className={mand ? 'on' : ''} onClick={() => onMand(true)}>Mandatory</button>
    <button data-v="opt" className={!mand ? 'on' : ''} onClick={() => onMand(false)}>Optional</button>
  </div>
)
const HSeg = ({ hard, onHard }) => (
  <div className="mseg">
    <button data-v="hard" className={hard ? 'on' : ''} onClick={() => onHard(true)}>Hard check</button>
    <button data-v="soft" className={!hard ? 'on' : ''} onClick={() => onHard(false)}>Soft check</button>
  </div>
)
function StandardCard({ params, data, toggleStd, setMand, setHard }) {
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const list = q.trim() ? params.filter((p) => p.code.toLowerCase().includes(q.toLowerCase()) || (p.description || '').toLowerCase().includes(q.toLowerCase())) : params

  const columns = [
    { field: 'enable', headerName: 'Enable', width: 90, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: ({ row }) => <Switch size="small" checked={data.sel.includes(row.code)} onChange={() => toggleStd(row.code)} /> },
    // Rich two-line cell — mono code + plain-English description (the reference's cell hierarchy).
    { field: 'code', headerName: 'Parameter', flex: 1.6, minWidth: 220,
      renderCell: ({ row }) => <CodeCell code={row.code} description={row.description} /> },
    { field: 'mandatory', headerName: 'Mandatory?', width: 215, sortable: false, align: 'center', headerAlign: 'center',
      renderHeader: () => <span>Mandatory? <Tip title={MAND_TIP} /></span>,
      renderCell: ({ row }) => data.sel.includes(row.code)
        ? <MSeg mand={!!data.mand[row.code]} onMand={(v) => setMand(row.code, v)} />
        : <CellLine muted>—</CellLine> },
    { field: 'hard', headerName: 'Check type', width: 235, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: ({ row }) => data.sel.includes(row.code)
        ? <HSeg hard={data.hard?.[row.code] !== false} onHard={(v) => setHard(row.code, v)} />
        : <CellLine muted>—</CellLine> },
  ]

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 4 }}><h2 style={{ margin: 0 }}>2 · Standard QC parameters</h2><div className="spacer" /><span className="counter"><b>{data.sel.length}</b> selected</span></div>
      <div className="ctx">Toggle on the checks that should run on <b>every</b> return for this merchant. (The courier-specific mapping lives in the <b>Courier QC Master</b>.)</div>
      <TextField
        size="small" placeholder="Search parameter…" value={q} onChange={(e) => setQ(e.target.value)}
        sx={{ ...searchFieldSx, mb: 1.5 }} InputProps={SearchAdornment}
      />
      {!list.length ? (
        <div className="empty"><h3>No parameters</h3><p>Master parameters come from the Courier QC Master.</p></div>
      ) : (
        <ProzoDataGrid
          columns={columns}
          rows={list}
          getRowId={(row) => row.code}
          rowCount={list.length}
          pagination
          paginationMode="client"
          page={page}
          setPage={setPage}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          getRowHeight={() => 'auto'}
          flat={false}
          sx={gridSx}
        />
      )}
    </div>
  )
}

/* Step 2 (reason-based) — reason-FIRST. Each return reason is a block: enter the verbatim
   reason, then add the QC parameters that run for THAT reason, each with its own
   Mandatory/Optional toggle. Mandatory/optional is per-reason (not global). Data shape
   matches §3.2 reasonMap: [{ returnReason, params:[{code, mandatory}] }]. */
// Plain MUI Select (battle-tested listbox) instead of a native <select> or hand-rolled
// dropdown. Value is always '' — this is a momentary "pick one to add" action, not a
// persisted field, so renderValue always shows the placeholder regardless of selection.
// The QC-parameter picker uses the module's shared searchable dropdown (SearchSelect), so it looks
// identical to the merchant picker and the Step-2 mapping dropdown. value is always null: this is
// an "add one" action that resets after each pick, not a persisted selection.
function ReasonParamAdder({ options, onAdd }) {
  return (
    <SearchSelect
      options={options}
      value={null}
      resetOnSelect
      disabled={!options.length}
      placeholder={options.length ? 'Search or add QC parameter…' : 'All QC parameters added'}
      getOptionLabel={(p) => humanize(p.code)}
      getOptionKey={(p) => p.code}
      getOptionDescription={(p) => (hasMeaningfulDesc(p.code, p.description) ? p.description : '')}
      isOptionEqualToValue={(a, b) => a.code === b.code}
      filterText={(p) => `${humanize(p.code)} ${p.code} ${p.description || ''}`}
      noOptionsText="No QC parameters match"
      onChange={(p) => { if (p) onAdd(p.code) }}
      sx={{ maxWidth: 440 }}
    />
  )
}

function ReasonConfigCard({ params, data, couriers = [], addReason, removeReason, setReasonLabel, addReasonParam, removeReasonParam, setReasonParamMand, setReasonParamHard }) {
  const byCode = new Map(params.map((p) => [p.code, p]))
  const support = (code) => couriers.filter((c) => (byCode.get(code)?.cpMap || []).some((m) => String(m.cpId) === String(c.cpId)))
  // Compact table (one row per attached check) instead of big per-parameter cards — same labelled
  // controls (column headers name them), far less vertical space. Factory closes over the reason id.
  const paramColumns = (rid) => [
    // Rich two-line cell (humanized code + plain-English description) — the ORDER DETAILS treatment.
    { field: 'code', headerName: 'QC check', flex: 1.6, minWidth: 210, sortable: false,
      renderCell: ({ row }) => <CodeCell code={row.code} description={byCode.get(row.code)?.description} /> },
    { field: 'req', headerName: 'Requirement', width: 215, sortable: false,
      renderCell: ({ row }) => <MSeg mand={row.mandatory} onMand={(v) => setReasonParamMand(rid, row.code, v)} /> },
    { field: 'hard', headerName: 'Check type', width: 235, sortable: false,
      renderCell: ({ row }) => <HSeg hard={row.hard !== false} onHard={(v) => setReasonParamHard(rid, row.code, v)} /> },
    // Coverage badge — colour-coded like the reference table's score/zone pills.
    { field: 'couriers', headerName: 'Couriers', width: 130, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: ({ row }) => {
        if (!couriers.length) return <CellLine muted>—</CellLine>
        const n = support(row.code).length
        const color = n === couriers.length ? 'success' : n > 0 ? 'warning' : 'default'
        return <Chip size="small" variant="outlined" color={color} label={`${n} / ${couriers.length}`}
          sx={{ fontWeight: 700 }}
          title={couriers.map((c) => `${c.name}: ${support(row.code).some((s) => s.name === c.name) ? 'supported' : 'not supported'}`).join('\n')} />
      } },
    { field: 'remove', headerName: '', width: 52, sortable: false, align: 'center',
      renderCell: ({ row }) => (
        <IconButton size="small" title={`Remove ${row.code}`} onClick={() => removeReasonParam(rid, row.code)}
          sx={{ color: 'text.disabled', '&:hover': { color: 'error.main', bgcolor: 'error.lighter' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) },
  ]
  return (
    <div className="card">
      <h2>2 · Configure return-reason-wise QC parameters</h2>
      <div className="ctx">The customer’s <b>return reason</b> decides which quality checks the courier runs at pickup. Set up each reason below.</div>
      {/* How-it-works — a plain instructional line, not selectable chips */}
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
        <b>1.</b> Add a return reason&nbsp;&nbsp;→&nbsp;&nbsp;<b>2.</b> Attach the QC checks it needs&nbsp;&nbsp;→&nbsp;&nbsp;<b>3.</b> Mark each Mandatory or Optional
      </Typography>

      {!data.reasons.length && <div className="empty" style={{ padding: 24 }}><h3>No return reasons yet</h3><p>Add a return reason below, then attach the QC parameters that apply to it.</p></div>}

      {data.reasons.map((r, i) => {
        const avail = params.filter((mp) => !r.params.some((p) => p.code === mp.code))
        return (
          <div key={r.id} className="rblock">
            <div className="rhead">
              <span className="rnum">{i + 1}</span>
              <input className="rin" value={r.label} placeholder="Customer return reason (e.g. “Product is damaged”)…" onChange={(e) => setReasonLabel(r.id, e.target.value)} />
              <button className="delx" title="Remove reason" onClick={() => removeReason(r.id)}>🗑</button>
            </div>
            {r.params.length ? (
              <Box sx={{ mt: 1.5, mb: 1.5 }}>
                <ProzoDataGrid
                  columns={paramColumns(r.id)}
                  rows={r.params}
                  getRowId={(row) => row.code}
                  rowCount={r.params.length}
                  pagination paginationMode="client"
                  page={0} setPage={() => {}} sizePerPage={100} setSizePerPage={() => {}}
                  hideFooter autoHeight flat={false} sx={gridSx}
                />
              </Box>
            ) : <div style={{ color: 'var(--muted)', fontSize: 12, margin: '12px 0' }}>No QC checks yet — add one below.</div>}
            <ReasonParamAdder options={avail} onAdd={(code) => addReasonParam(r.id, code)} />
          </div>
        )
      })}

      <button className="btn ghost" style={{ marginTop: 4 }} onClick={addReason}>+ Add return reason</button>
    </div>
  )
}

/* #7 · Courier-wise QC availability — which RVP-QC couriers can perform each of the
   merchant's QC checks (from each param's cpMap). Courier-native parameter names are never
   shown, only master codes. Allocation source is backend-pending, so this shows ALL RVP-QC
   partners; per-merchant allocated-partner flagging lands when that API exists. */
function AvailabilityCard({ selectedCodes = [], params, couriers, allocatedCouriers = new Set(), allocUploaded = false }) {
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const byCode = new Map(params.map((p) => [p.code, p]))
  const supports = (code, c) => (byCode.get(code)?.cpMap || []).some((m) => String(m.cpId) === String(c.cpId))
  const codes = [...new Set(selectedCodes)]
  // Match an allocated courier string (parent name or account code) to a grid parent column.
  const norm = (s) => String(s || '').toLowerCase().trim()
  const isAllocated = (name) => allocUploaded && [...allocatedCouriers].some((a) => {
    const an = norm(a); const pn = norm(name)
    return an === pn || an.startsWith(pn) || pn.startsWith(an)
  })

  const rows = codes.map((code) => ({ id: code, code }))
  const columns = [
    { field: 'code', headerName: 'QC check', minWidth: 200, flex: 1,
      renderCell: (params_) => <CodeText code={params_.row.code} /> },
    ...couriers.map((c) => {
      const alloc = isAllocated(c.name)
      return {
        field: c.name, width: 150, sortable: false, align: 'center', headerAlign: 'center',
        renderHeader: () => (
          <Box sx={{ textAlign: 'center', py: 0.5 }}>
            <CellLine bold>{c.name}</CellLine>
            {allocUploaded && <Chip label={alloc ? 'allocated' : 'not allocated'} size="small" color={alloc ? 'primary' : 'default'} variant={alloc ? 'filled' : 'outlined'} sx={{ mt: 0.5 }} />}
          </Box>
        ),
        renderCell: (params_) => supports(params_.row.code, c)
          ? <Chip label="✓" size="small" color="success" title={`${c.name} supports ${params_.row.code}`} />
          : <Chip label="—" size="small" variant="outlined" title={`${c.name} does not support ${params_.row.code}`} />,
      }
    }),
  ]

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 4 }}><h2 style={{ margin: 0 }}>Courier-wise QC availability</h2></div>
      {!couriers.length ? (
        <div className="ctx" style={{ marginBottom: 0 }}>No RVP-QC courier partners found.</div>
      ) : !codes.length ? (
        <div className="ctx" style={{ marginBottom: 0 }}>No QC checks selected yet — add checks above to see courier availability.</div>
      ) : (
        <ProzoDataGrid
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          rowCount={rows.length}
          pagination
          paginationMode="client"
          page={page}
          setPage={setPage}
          sizePerPage={sizePerPage}
          setSizePerPage={setSizePerPage}
          headerHeight={allocUploaded ? 64 : 48}
          flat={false}
          sx={gridSx}
        />
      )}
    </div>
  )
}

/* #8 · Allocation fallback sequence — the admin consents to each fallback step with a per-row
   toggle. N = the merchant's MANDATORY QC parameters; the full-N level is always attempted
   first (required). Each lower step (N-1 … 1) and the non-QC last resort has an "Acceptable?"
   toggle. If the non-QC step is not accepted, unmet orders become non-serviceable.
   Eligible couriers + pickup-pincode coverage per step come from the serviceability advisor
   (§3.3), falling back to cpMap for couriers while the advisor loads. */
function FallbackCard({ mandatoryCodes = [], params, couriers, advisor, advLoading, isAcc, onToggle, approved, onReview }) {
  // Per-step consent state is lifted to the editor (so Save can read it and gate on approval).
  const codes = [...new Set(mandatoryCodes)]
  const N = codes.length
  const byCode = new Map(params.map((p) => [p.code, p]))
  const covers = (c) => codes.filter((code) => (byCode.get(code)?.cpMap || []).some((m) => String(m.cpId) === String(c.cpId))).length
  const tiers = []
  for (let k = N; k >= 1; k--) tiers.push({ k, list: couriers.filter((c) => covers(c) === k) })
  const nonQcAccepted = isAcc('NONQC')
  // #9 advisor overlay — eligible parents + pincode coverage per step, keyed by mandatoryRequired.
  const advByK = new Map(); let advNonQc = null
  ;(advisor?.ladder || []).forEach((r) => { if (r.nonQc) advNonQc = r; else advByK.set(r.mandatoryRequired, r) })
  const hasAdv = !!advisor?.ladder
  const noAlloc = hasAdv && advisor.allocatedRvpCourierCount === 0
  const pin = (v) => advLoading ? '…' : (v == null ? '—' : Number(v).toLocaleString('en-IN'))
  // Qualifying couriers as a plain readable name list (like the reference's COURIER column text),
  // not chips.
  const couriersCell = (advRow, fallbackList) => {
    if (advLoading && !hasAdv) return <CellLine muted>…</CellLine>
    const names = hasAdv ? (advRow?.eligibleParents || []).map((p) => p.parent) : fallbackList.map((c) => c.name)
    return names.length
      ? <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.primary', lineHeight: 1.4, whiteSpace: 'normal' }}>{names.join(', ')}</Typography>
      : <CellLine muted>None</CellLine>
  }

  const fbRows = [
    ...tiers.map(({ k, list }) => ({ id: String(k), k, req: k === N, list, adv: advByK.get(k) })),
    { id: 'NONQC', nonQc: true },
  ]
  const fbColumns = [
    // Two-line step cell — bold level + muted role subline (the ORDER DETAILS hierarchy).
    { field: 'step', headerName: 'Fallback step', flex: 1, minWidth: 190, sortable: false,
      renderCell: ({ row: r }) => {
        const acc = r.nonQc ? nonQcAccepted : (r.req || isAcc(String(r.k)))
        const title = r.nonQc ? 'Non-QC pickup' : `${r.k} of ${N} check${r.k > 1 ? 's' : ''}`
        const sub = r.nonQc ? 'Last resort' : r.req ? 'Full QC level' : 'Partial fallback'
        return (
          <Box sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: acc ? 'text.primary' : 'text.disabled', textDecoration: acc ? 'none' : 'line-through' }}>{title}</Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>
          </Box>
        )
      } },
    // Toggle only — the full-QC level is always required, so its toggle is on + locked.
    { field: 'acceptable', headerName: 'Acceptable?', width: 130, sortable: false, align: 'center', headerAlign: 'center',
      renderCell: ({ row: r }) => {
        if (r.req) return <Switch size="small" checked disabled title="Full QC is always attempted first" />
        const key = r.nonQc ? 'NONQC' : String(r.k)
        const acc = r.nonQc ? nonQcAccepted : isAcc(String(r.k))
        return <Switch size="small" checked={acc} onChange={() => onToggle(key)} />
      } },
    { field: 'couriers', headerName: 'Couriers that qualify', flex: 1.2, minWidth: 200, sortable: false,
      renderCell: ({ row: r }) => r.nonQc ? <CellLine muted>Accepted without doorstep QC</CellLine> : couriersCell(r.adv, r.list) },
    // Serviceability as a colour-dot stat — green when there's coverage, grey at zero.
    { field: 'serviceability', headerName: `Serviceability (pincodes)${advLoading ? ' · loading…' : ''}`, width: 190, sortable: false,
      renderCell: ({ row: r }) => {
        const raw = r.nonQc ? advNonQc?.distinctPickupPincodes : r.adv?.distinctPickupPincodes
        const has = typeof raw === 'number' && raw > 0
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: has ? 'success.main' : 'grey.300' }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: has ? 'text.primary' : 'text.secondary' }}>{pin(raw)}</Typography>
          </Box>
        )
      } },
  ]

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 4 }}><h2 style={{ margin: 0 }}>Allocation fallback sequence</h2></div>
      {N === 0 ? (
        <div className="ctx" style={{ marginBottom: 0 }}>No <b>mandatory</b> QC checks configured yet — mark one or more checks <b>Mandatory</b> above to define the fallback sequence.</div>
      ) : (
        <>
          {noAlloc && (
            <Typography sx={{ fontSize: 11.5, color: '#B7791F', mb: 1.5 }}>
              ⚠ No allocated RVP couriers — serviceability shows 0. Set up the merchant’s reverse allocation for real coverage.
            </Typography>
          )}

          <ProzoDataGrid
            columns={fbColumns}
            rows={fbRows}
            getRowId={(row) => row.id}
            rowCount={fbRows.length}
            pagination
            paginationMode="client"
            page={0}
            setPage={() => {}}
            sizePerPage={100}
            setSizePerPage={() => {}}
            hideFooter
            autoHeight
            flat={false}
            sx={gridSx}
          />

          {!nonQcAccepted && (
            <Typography sx={{ fontSize: 11.5, color: 'error.main', mt: 1.5 }}>
              Non-QC pickup not accepted — orders no partner can service at an accepted level will become non-serviceable.
            </Typography>
          )}

          <div className="row" style={{ marginTop: 12, justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
            {approved ? (
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, fontWeight: 600, color: 'success.main' }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> Sequence approved
              </Typography>
            ) : (
              <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, color: 'text.secondary' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} /> Approval required before saving
              </Typography>
            )}
            <button className="btn ghost" onClick={onReview}>{approved ? 'Review again' : 'Review & approve sequence'}</button>
          </div>
        </>
      )}
    </div>
  )
}
